import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "react-notify-toast";
import ScrollBar from "../../../../../../../common/element/ScrollBar";
import Table from "../../../../../../../common/element/Table";
import BtnLight from "../../../../../../../common/element/Buttons/btnLight";
import BtnPrimary from "../../../../../../../common/element/Buttons/btnPrimary";
import Switcher from "../../../../../../../common/element/Switcher";
import Input from "../../../../../../../common/element/Input";
import Button from "../../../../../../../common/element/Buttons";
import Label from "../../../../../../../common/element/Label";
import {GetAllProviderTypes, AddCustomProviderType} from "../../../../../../../actions/Admin";
import useUnauthorizedHandler from "../../../../../../../common/snippet/useUnauthorizedHandler";

const ProviderPricing = forwardRef((props, ref) => {
  const { Tr, Th, Td, THead, TBody } = Table;
  const dispatch = useDispatch();
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  const ipAddress = useSelector((state) => state?.ipAddress?.ip);
  const [pricingOverride, setPricingOverride] = useState(true);
  const [providerTypes, setProviderTypes] = useState([]);
  const [originalProviderTypes, setOriginalProviderTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onSwitcherToggle = (e, identifier) => {
    if (identifier === "pricingOverride") {
      setPricingOverride(!e);
    }
  };

  const fetchProviderTypes = () => {
    setLoading(true);
    const params = props?.clientOrgId ? { client_org_id: props.clientOrgId } : {};
    dispatch(GetAllProviderTypes(params)).then((response) => {
      if (response?.success === true) {
        console.log("respsososo", response.data)
        const data = response?.data?.rows?.map((item) => ({
          provider_type_id: item?.provider_type_id,
          provider_type_name: item?.provider_type_name,
          assignment_fee: item?.assignment_fee || "",
          license_fee: item?.licence_fee || "",
          can_invite_workers: item?.can_invite_workers || false,
        }));
        setProviderTypes(data);
        setOriginalProviderTypes(data);
      } else if (response?.status === 401) {
        handleUnauthorizedAccess();
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchProviderTypes();
  }, []);

  const handleInputChange = (providerId, field, value) => {
    setProviderTypes((prev) =>
      prev.map((item) =>
        item.provider_type_id === providerId
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const handleReset = (providerId) => {
    const originalItem = originalProviderTypes.find(
      (item) => item.provider_type_id === providerId
    );
    if (originalItem) {
      setProviderTypes((prev) =>
        prev.map((item) =>
          item.provider_type_id === providerId
            ? {
                ...item,
                assignment_fee: originalItem.assignment_fee,
                license_fee: originalItem.license_fee,
              }
            : item
        )
      );
    }
  };

  const SavePricing = async () => {
    if (!props?.clientOrgId) {
      console.error("Client organization ID is missing");
      return;
    }

    setSaving(true);
    
    try {
      // Filter only modified provider types
      const modifiedProviderTypes = providerTypes.filter((providerType) => {
        const original = originalProviderTypes.find(
          (item) => item.provider_type_id === providerType.provider_type_id
        );
        
        if (!original) return false;
        
        // Check if assignment_fee or license_fee has changed
        return (
          providerType.assignment_fee !== original.assignment_fee ||
          providerType.license_fee !== original.license_fee
        );
      });

      // If no changes, just close the dialog
      if (modifiedProviderTypes.length === 0) {
        console.log("No changes to save");
        props?.userSettingHide();
        setSaving(false);
        return;
      }

      // Send only modified provider types to the API
      const promises = modifiedProviderTypes.map((providerType) => {
        const data = {
          provider_type_id: providerType.provider_type_id,
          client_org_id: props.clientOrgId,
          licence_fee: parseFloat(providerType.license_fee) || 0,
          assignment_fee: parseFloat(providerType.assignment_fee) || 0,
          worker_fee: 0, // Default to 0 as per API requirement
          // change_effective_date can be added if needed
        };
        
        return dispatch(AddCustomProviderType(data, ipAddress));
      });

      const responses = await Promise.all(promises);
      
      // Check if all requests were successful
      const allSuccessful = responses.every(response => response?.success === true);
      
      if (allSuccessful) {
        console.log("Custom provider pricing saved successfully");
        // Update original values to reflect saved state
        setOriginalProviderTypes([...providerTypes]);
        // Show success notification
        notify.show(
          `Provider pricing updated successfully for ${modifiedProviderTypes.length} provider type${modifiedProviderTypes.length > 1 ? 's' : ''}`,
          "success",
          3000
        );
      } else {
        // Check for unauthorized access
        const hasUnauthorized = responses.some(response => response?.status === 401);
        if (hasUnauthorized) {
          handleUnauthorizedAccess();
        } else {
          console.error("Some pricing updates failed");
          notify.show(
            "Failed to update some provider pricing. Please try again.",
            "error",
            3000
          );
        }
      }
    } catch (error) {
      console.error("Error saving pricing:", error);
      notify.show(
        "An error occurred while saving pricing. Please try again.",
        "error",
        3000
      );
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    SavePricing,
  }));

  return (
    <React.Fragment>
      <div>
        <div className="h-500 pr-1">
          <ScrollBar>
            <div className="pr-4 mt-5">
              <div className="flex justify-between flex-col mb-5 border-b border-gray-200 pb-5">
              <h5 className="text-xl text-gray-900 font-semibold mb-1">
                  Provider Pricing
                </h5>
                <p className="text-gray-600">
                  Manage provider pricing
                </p>
              </div>
              <div className="flex items-center border-b border-gray-200 pb-5 mb-5">
                <div className="flex flex-wrap items-center">
                  <Label className="text-gray-700 font-semibold w-52 font-sm">
                    Override default pricing?
                  </Label>
                
                <Switcher
                    onChange={(e) => onSwitcherToggle(e, "pricingOverride")}
                    checked={pricingOverride}
                    value={pricingOverride}
                />
                </div>
              </div>
              <div className="overflow-hidden border rounded-lg border-gray-200">
              <Table>
                <THead>
                  <Tr>
                    <Th className="w-1/3">Provider Type</Th>
                    <Th className="w-1/4">Assignment Fee</Th>
                    <Th className="w-1/4">License Fee</Th>
                    <Th className="w-1/6"></Th>
                  </Tr>
                </THead>
                <TBody>
                  {loading ? (
                    <Tr>
                      <Td colSpan="4" className="text-center py-4">
                        Loading provider types...
                      </Td>
                    </Tr>
                  ) : providerTypes.length > 0 ? (
                    providerTypes.map((item) => (
                      <Tr key={item?.provider_type_id}>
                        <Td className="font-medium text-gray-900">
                          {item?.provider_type_name}
                        </Td>
                        <Td>
                          <div className="relative">
                            <span className="absolute mt-2 ml-2">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 13.3337C5 15.1746 6.49238 16.667 8.33333 16.667H11.6667C13.5076 16.667 15 15.1746 15 13.3337C15 11.4927 13.5076 10.0003 11.6667 10.0003H8.33333C6.49238 10.0003 5 8.50794 5 6.66699C5 4.82604 6.49238 3.33366 8.33333 3.33366H11.6667C13.5076 3.33366 15 4.82604 15 6.66699M10 1.66699V18.3337"
                                  stroke="#667085"
                                  stroke-width="1.66667"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </span>
                            <Input
                              type="number"
                              placeholder="$0.00"
                              className="w-full h-9 pl-8"
                              value={item?.assignment_fee}
                              inputchangehandler={(e) =>
                                handleInputChange(
                                  item?.provider_type_id,
                                  "assignment_fee",
                                  e
                                )
                              }
                              disabled={!pricingOverride}
                            />
                          </div>
                        </Td>
                        <Td>
                          <div className="relative">
                            <span className="absolute mt-2 ml-2">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5 13.3337C5 15.1746 6.49238 16.667 8.33333 16.667H11.6667C13.5076 16.667 15 15.1746 15 13.3337C15 11.4927 13.5076 10.0003 11.6667 10.0003H8.33333C6.49238 10.0003 5 8.50794 5 6.66699C5 4.82604 6.49238 3.33366 8.33333 3.33366H11.6667C13.5076 3.33366 15 4.82604 15 6.66699M10 1.66699V18.3337"
                                  stroke="#667085"
                                  stroke-width="1.66667"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                            </span>
                            <Input
                              type="number"
                              placeholder="$0.00"
                              className="w-full h-9 pl-8"
                              value={item?.license_fee}
                              inputchangehandler={(e) =>
                                handleInputChange(
                                  item?.provider_type_id,
                                  "license_fee",
                                  e
                                )
                              }
                              disabled={!pricingOverride}
                            />
                          </div>
                        </Td>
                        <Td>
                          <span className="flex gap-2 justify-center">
                            <Button
                              className="text-gray-600"
                              title="Reset to default"
                              onClick={() => handleReset(item?.provider_type_id)}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M9 3h6m-12 3h18m-2 0-.701 10.52c-.105 1.578-.158 2.367-.499 2.965a3 3 0 0 1-1.298 1.215c-.62.3-1.41.3-2.993.3h-3.018c-1.582 0-2.373 0-2.993-.3A3 3 0 0 1 6.2 19.985c-.34-.598-.394-1.387-.499-2.966L5 6m5 4.5v5m4-5v5"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </Button>
                          </span>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan="4" className="text-center py-4 text-gray-500">
                        No provider types found
                      </Td>
                    </Tr>
                  )}
                </TBody>
              </Table>
            </div>
             

              
            </div>
          </ScrollBar>
        </div>
        <div className="flex p-6 justify-end border-gray-200">
          <BtnLight
            onClick={() => props?.userSettingHide()}
            className="w-auto px-5 mr-3"
            variant="plain"
          >
            Close
          </BtnLight>
          <BtnPrimary
            onClick={() => SavePricing()}
            className="w-auto px-5"
            disabled={saving || !pricingOverride}
          >
            {saving ? "Saving..." : "Save changes"}
          </BtnPrimary>
        </div>
      </div>
    </React.Fragment>
  );
});

export default ProviderPricing;
